import type { Block } from '@/content/blocks'
import type { CheckItem } from '@/content/checks'
import type { Ebook } from '@/content/ebookBuilder'
import { published } from '@/content/drafts'

// Imported AND re-exported, not `export type { Ebook } from '...'` — that form
// re-exports without binding the name locally, so `Ebook` in this file's own
// signatures below would resolve to whatever else is in scope. reports.ts hit
// exactly that on 2026-08-30 and the mismatch surfaced three files away.
export type { Ebook }

/**
 * Every e-book, one file each.
 *
 * The last of the four catalogues, and the only one that never got a glob when
 * the others did in August — so this adds the glob, the seventeen files and the
 * builder in one change rather than half of it.
 *
 * TWO REASONS, and the second is why the other three were revisited. Collisions:
 * two AI-editor runs adding two books rewrote the same region and git refused
 * both rebases. Cost: an edit is billed `context × turns × cache-read`, which is
 * 50–62% of what a request costs, and a book edit was loading the whole
 * catalogue to change one line.
 *
 * Add `src/content/ebooks/<slug>.ts`:
 *
 *     import { ebook, pdf } from '@/content/ebookBuilder'
 *     import type { Ebook } from '@/content/ebookBuilder'
 *     export const added: Ebook = ebook('acme', 'Acme', 'acme-cover.jpg', { … })
 *     export const order = 17
 *
 * `added` rather than `ebook`, because the entry is BUILT by a function called
 * `ebook` imported into the same file and a const of that name would shadow it.
 * Import the builder, never `ebooks.ts`: this module is mid-initialisation when
 * the eager glob evaluates your file, which is a `ReferenceError` at prerender
 * rather than a typecheck error.
 *
 * `order` then slug, so the library grid never depends on filesystem iteration.
 * **A file with no `order` sorts last** — the seventeen carry an explicit 0–16,
 * which is the live index's own order and a marketing decision, not a rendering
 * one.
 */
const ADDED = import.meta.glob<{ added: Ebook; order?: number }>(
  './ebooks/*.ts',
  { eager: true },
)

/**
 * Every entry, drafts included. `EBOOKS` below is what ships.
 *
 * The split is deliberate and mirrors `LOCALES` / `ALL_LOCALES`: the plain
 * name is the published list, so a consumer that has never heard of drafts is
 * correct by default, and the only readers of this one are the build's draft
 * report and anything that genuinely needs the roster.
 */
export const ALL_EBOOKS: Ebook[] =
Object.values(ADDED)
  .filter((m) => m.added)
  .sort(
    (a, b) =>
      (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      || a.added.slug.localeCompare(b.added.slug),
  )
  .map((m) => m.added)

export const EBOOKS: Ebook[] = published(ALL_EBOOKS)

/** The e-books index page's slug, in the one locale it exists in. */
export const EBOOKS_INDEX = 'impact-labs/ebooks'

/**
 * The pages whose e-book list is the card grid rather than their own blocks.
 *
 * Same provenance and the same shape as `PAGE_REPORTS` — attached in
 * `src/legacy.ts` at load time, not written into the imported JSON, because that
 * JSON is the record of what digitopia.co said and the grid is what we are saying
 * instead.
 *
 * **Unlike the reports, it replaces only part of the page.** The reports index
 * was one block and nothing else; this page has a heading and a lede above its
 * list and a newsletter block below it, so only the list itself is swapped —
 * see `splitEbookBlocks`.
 */
export const PAGE_EBOOKS: Record<string, Ebook[]> = {
  [`en/${EBOOKS_INDEX}`]: EBOOKS,
}

/** Where an e-book's page lives, for a reader of a given locale. */
export function ebookHref(locale: string, book: Ebook): string {
  return book.href ?? `/${locale}/${EBOOKS_INDEX}/${book.slug}`
}

/** The e-books that need a document generated for them — everything without a page already. */
export const EBOOKS_NEEDING_PAGE: Ebook[] = EBOOKS.filter((b) => !b.href)

const LIST_COVERS = new Set(EBOOKS.map((b) => b.imported))
const LIST_TITLES = new Set(EBOOKS.map((b) => b.title))
const LIST_LINKS = new Set(
  EBOOKS.flatMap((b) => [b.download, b.href]).filter((h): h is string => Boolean(h)),
)

/** Whether a block is one of the seventeen cover / title / download triples. */
function isEbookBlock(block: Block): boolean {
  if (block.kind === 'image') return LIST_COVERS.has(block.src)
  if (block.kind === 'heading') return block.level === 3 && LIST_TITLES.has(block.text)
  if (block.kind === 'cta') return LIST_LINKS.has(block.href)
  return false
}

/**
 * The page's own words, either side of the list the grid replaces.
 *
 * The index is not only its e-books: it opens with "Master the Art of
 * Transformation" and a lede, and closes with the newsletter block. Dropping
 * every block the way the reports page does would take those with it, so the
 * split is by position — everything before the first e-book block, everything
 * after the last one, and the run between them is what the cards say instead.
 *
 * Matched against the catalogue rather than by counting, so a re-import that
 * adds an e-book leaves it visible as a figure until it is listed here, rather
 * than silently swallowing whatever sat in that slot.
 */
export function splitEbookBlocks(blocks: Block[]): { intro: Block[]; outro: Block[] } {
  const first = blocks.findIndex(isEbookBlock)
  if (first === -1) return { intro: blocks, outro: [] }
  let last = blocks.length - 1
  while (last > first && !isEbookBlock(blocks[last])) last--
  return {
    intro: blocks.slice(0, first),
    outro: blocks.slice(last + 1).filter((b) => !isEbookBlock(b)),
  }
}

/**
 * The e-book gaps, in the shape `build-manifest.json` records copy checks in, so
 * `pnpm verify` refuses a production build while any e-book is still missing its
 * download link, its own words or a confirmed address. Without this they would be
 * visible in a preview and silently stripped from production, which is the exact
 * failure the check channel exists to prevent.
 */
export function ebookChecks(): CheckItem[] {
  return EBOOKS.filter((b) => b.check).map((b) => ({
    locale: 'en',
    page: `${EBOOKS_INDEX}/${b.slug}`,
    section: 'ebook',
    text: b.check as string,
    // A missing download link or summary is a hole to fill, not a claim
    // somebody has to stand behind — see `CheckItem` in content/checks.ts.
    severity: 'asset',
  }))
}
